import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React from "react";
import { useMemo, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const JOIN_TYPES = ["LEFT", "RIGHT", "INNER", "FULL"];

export const JoinEditor = ({
  initial,
  tableOptions,
  sourceTableNames,
  otherDisplayNames = [],
  getColumnsForTable,
  onAccept,
  onCancel,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  // The editable name is the display name; initial.joinName is the internal
  // name, which the editor carries through untouched.
  const [joinDisplayName, setJoinDisplayName] = useState(
    initial.joinDisplayName ?? initial.joinName ?? initial.name ?? ""
  );
  const [leftTable, setLeftTable] = useState(initial.leftTable);
  const [rightTable, setRightTable] = useState(initial.rightTable);
  const [joinType, setJoinType] = useState(initial.joinType);
  const [leftColumn, setLeftColumn] = useState(null);
  const [rightColumn, setRightColumn] = useState(null);
  const [columnPairs, setColumnPairs] = useState([
    ...(initial.columns ?? initial.columnPairs ?? []),
  ]);

  const leftColumns = useMemo(() => getColumnsForTable(leftTable), [
    leftTable,
    getColumnsForTable,
  ]);
  const rightColumns = useMemo(() => getColumnsForTable(rightTable), [
    rightTable,
    getColumnsForTable,
  ]);

  const leftColumnOptions = useMemo(
    () =>
      leftColumns.map((col) => ({
        id: col.internalName,
        label: col.displayName || col.internalName,
      })),
    [leftColumns]
  );
  const rightColumnOptions = useMemo(
    () =>
      rightColumns.map((col) => ({
        id: col.internalName,
        label: col.displayName || col.internalName,
      })),
    [rightColumns]
  );

  const handleLeftTableChange = (e) => {
    setLeftTable(e.target.value);
    setLeftColumn(null);
  };
  const handleRightTableChange = (e) => {
    setRightTable(e.target.value);
    setRightColumn(null);
  };

  const canAddPair = leftColumn !== null && rightColumn !== null;

  const handleAddPair = () => {
    if (!canAddPair) return;
    setColumnPairs((prev) => [
      ...prev,
      {
        left: leftColumn.id,
        right: rightColumn.id,
      },
    ]);
    setLeftColumn(null);
    setRightColumn(null);
  };

  const handleRemovePair = (index) => {
    setColumnPairs((prev) => prev.filter((_, i) => i !== index));
  };

  const takenNames = useMemo(() => new Set(otherDisplayNames), [
    otherDisplayNames,
  ]);

  // The first unmet condition, in the order the messages are listed, or null
  // when the join can be accepted.
  const acceptBlocker = useMemo(() => {
    if (!leftTable || !rightTable) {
      return translateLabels(labels.JOIN_EDITOR_NEEDS_TABLES, {
        default: "Tables need to be specified",
      });
    }
    if (columnPairs.length < 1) {
      return translateLabels(labels.JOIN_EDITOR_NEEDS_COLUMN_PAIR, {
        default: "At least one pair of columns are needed",
      });
    }
    const trimmedName = joinDisplayName.trim();
    if (trimmedName !== "" && takenNames.has(trimmedName)) {
      return translateLabels(labels.JOIN_EDITOR_NAME_NOT_UNIQUE, {
        default: "Name is not unique",
      });
    }
    if (trimmedName === "") {
      return translateLabels(labels.JOIN_EDITOR_NAME_BLANK, {
        default: "Name is blank",
      });
    }
    return null;
  }, [
    leftTable,
    rightTable,
    columnPairs,
    joinDisplayName,
    takenNames,
    translateLabels,
  ]);

  const canAccept = acceptBlocker === null;

  const handleAccept = () => {
    if (!canAccept) return;
    onAccept({
      joinName: initial.joinName,
      joinDisplayName: joinDisplayName.trim(),
      leftTable,
      rightTable,
      joinType,
      columns: [...columnPairs],
    });
  };

  const sourceOptions = tableOptions.filter((opt) =>
    sourceTableNames.has(opt.value)
  );
  const joinOptions = tableOptions.filter(
    (opt) => !sourceTableNames.has(opt.value)
  );
  const hasJoinOptions = joinOptions.length > 0;

  const renderTableSelectItems = () => {
    const items = [];
    if (hasJoinOptions) {
      items.push(
        <ListSubheader key={"__tables_header"} sx={{ lineHeight: "32px" }}>
          {translateLabels(labels.JOIN_EDITOR_TABLES_HEADER, {
            default: "Tables",
          })}
        </ListSubheader>
      );
    }
    sourceOptions.forEach((opt) => {
      items.push(
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      );
    });
    if (hasJoinOptions) {
      items.push(
        <ListSubheader key={"__joins_header"} sx={{ lineHeight: "32px" }}>
          {translateLabels(labels.JOIN_EDITOR_JOINS_HEADER, {
            default: "Joins",
          })}
        </ListSubheader>
      );
      joinOptions.forEach((opt) => {
        items.push(
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        );
      });
    }
    return items;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: 640,
        p: 2,
      }}
    >
      {/* Name field */}
      <TextField
        size={"small"}
        label={translateLabels(labels.JOIN_EDITOR_NAME, { default: "Name" })}
        value={joinDisplayName}
        onChange={(e) => setJoinDisplayName(e.target.value)}
        fullWidth
        required
      />

      {/* Table row: left table, join type, right table */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={"subtitle2"}
            sx={{ mb: 1, color: "text.secondary" }}
          >
            {translateLabels(labels.JOIN_EDITOR_LEFT, { default: "Left" })}
          </Typography>
          <FormControl size={"small"} fullWidth>
            <InputLabel id={"join-left-table-label"}>
              {translateLabels(labels.JOIN_EDITOR_TABLE, { default: "Table" })}
            </InputLabel>
            <Select
              labelId={"join-left-table-label"}
              variant={"outlined"}
              value={leftTable}
              label={translateLabels(labels.JOIN_EDITOR_TABLE, {
                default: "Table",
              })}
              onChange={handleLeftTableChange}
            >
              {renderTableSelectItems()}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <FormControl size={"small"} fullWidth>
            <InputLabel id={"join-type-label"}>
              {translateLabels(labels.JOIN_EDITOR_JOIN, { default: "Join" })}
            </InputLabel>
            <Select
              labelId={"join-type-label"}
              variant={"outlined"}
              value={joinType}
              label={translateLabels(labels.JOIN_EDITOR_JOIN, {
                default: "Join",
              })}
              onChange={(e) => setJoinType(e.target.value)}
            >
              {JOIN_TYPES.map((jt) => (
                <MenuItem key={jt} value={jt}>
                  {jt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={"subtitle2"}
            sx={{ mb: 1, color: "text.secondary" }}
          >
            {translateLabels(labels.JOIN_EDITOR_RIGHT, { default: "Right" })}
          </Typography>
          <FormControl size={"small"} fullWidth>
            <InputLabel id={"join-right-table-label"}>
              {translateLabels(labels.JOIN_EDITOR_TABLE, { default: "Table" })}
            </InputLabel>
            <Select
              labelId={"join-right-table-label"}
              variant={"outlined"}
              value={rightTable}
              label={translateLabels(labels.JOIN_EDITOR_TABLE, {
                default: "Table",
              })}
              onChange={handleRightTableChange}
            >
              {renderTableSelectItems()}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Added column pairs */}
      {columnPairs.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
          }}
        >
          {columnPairs.map((pair, index) => {
            const leftDef = leftColumns.find(
              (c) => c.internalName === pair.left
            );
            const rightDef = rightColumns.find(
              (c) => c.internalName === pair.right
            );
            return (
              <Box
                key={index}
                sx={{ display: "flex", gap: 2, alignItems: "center" }}
              >
                <Chip
                  label={leftDef?.displayName || pair.left}
                  size={"small"}
                  variant={"outlined"}
                  sx={{ flex: 1, justifyContent: "flex-start" }}
                />
                <Typography
                  variant={"caption"}
                  sx={{
                    width: 100,
                    flexShrink: 0,
                    textAlign: "center",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {"="}
                </Typography>
                <Chip
                  label={rightDef?.displayName || pair.right}
                  size={"small"}
                  variant={"outlined"}
                  sx={{ flex: 1, justifyContent: "flex-start" }}
                />
                <IconButton
                  size={"small"}
                  onClick={() => handleRemovePair(index)}
                  sx={{ flexShrink: 0, ml: -0.5 }}
                >
                  <CloseIcon fontSize={"small"} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Column selection row */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            size={"small"}
            options={leftColumnOptions}
            value={leftColumn}
            onChange={(_, newValue) => setLeftColumn(newValue)}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            disabled={!leftTable}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translateLabels(labels.JOIN_EDITOR_COLUMN, {
                  default: "Column",
                })}
              />
            )}
          />
        </Box>
        <Box
          sx={{
            width: 100,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            size={"small"}
            isDisabled={!canAddPair}
            onClick={handleAddPair}
            startIcon={<AddIcon fontSize={"small"} />}
          >
            {translateLabels(labels.JOIN_EDITOR_ADD, { default: "Add" })}
          </Button>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            size={"small"}
            options={rightColumnOptions}
            value={rightColumn}
            onChange={(_, newValue) => setRightColumn(newValue)}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            disabled={!rightTable}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translateLabels(labels.JOIN_EDITOR_COLUMN, {
                  default: "Column",
                })}
              />
            )}
          />
        </Box>
      </Box>

      {/* Accept / Cancel */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button size={"small"} color={"secondary"} onClick={onCancel}>
          {translateLabels(labels.JOIN_EDITOR_CANCEL, { default: "Cancel" })}
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {acceptBlocker && (
            <Typography variant={"caption"} color={"error"}>
              {acceptBlocker}
            </Typography>
          )}
          <Button size={"small"} isDisabled={!canAccept} onClick={handleAccept}>
            {translateLabels(labels.JOIN_EDITOR_ACCEPT, { default: "Accept" })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
